import { TFunction } from 'next-i18next'
import React, { ReactChild, useMemo, Fragment } from 'react'
import { Col, Container, OverlayTrigger, Popover, Row } from 'react-bootstrap'
import styled from 'styled-components'
import { Trans, useTranslation } from '~/i18n'
import Box from '~/components/UI/Box'
import Responsive from '~/components/UI/Responsive'
import Text from '~/components/UI/Text'
import breakpoints from '~/components/UI/breakpoints'
import { observer } from 'mobx-react-lite'
import config from '~/stores/Config'

const ROW_HEIGTH = 60
const MyContainer = styled(Container)`
  margin-bottom: 30px;
  ${breakpoints.md} {
    margin-bottom: 60px;
  }
`

const Content = styled(Box)`
  background-color: #ebf3ff;
  border-radius: 24px;
  padding: 16px;
  ${breakpoints.lg} {
    padding-left: 48px;
  }
`

const getContent = (t: TFunction) => ({
  title: {
    saving: t('Tích Luỹ'),
    cd: t('CC Tiền Gửi'),
    mf: t('CC Quỹ'),
    re: t('Bất Động Sản'),
    isTitle: true,
  },
  [t('Thời hạn đầu tư')]: {
    saving: t('Linh hoạt'),
    cd: t('12 tháng'),
    mf: t('12 tháng'),
    re: t('Trên 18 tháng'),
    tooltip: t(
      'Bạn có thể rút vốn bất kỳ khi nào. Tuy nhiên các sản phẩm đầu tư cần có thời gian để lợi nhuận đạt mức tối ưu nhất.'
    ),
  },
  [t('Lợi nhuận (năm)')]: {
    saving: (
      <CenterView>
        {config.saving?.interest}% {config.saving?.promo_interest && <DiscountBadge>{t('Ưu đãi')}</DiscountBadge>}
      </CenterView>
    ),
    cd: config.cd?.interest + '%',
    mf: config.mf?.interest + '%',
    re: config.re?.interest + '%',
    tooltip: t(
      'Lợi nhuận của “Tích Luỹ” và “Chứng Chỉ Tiền Gửi” là lợi nhuận cố định. Lợi nhuận của "Chứng Chỉ Quỹ" và “Bất Động Sản” là lợi nhuận ước tính trung bình (lợi nhuận thực tế còn phụ thuộc vào từng sản phẩm).'
    ),
  },
  [t('Vốn đầu tư tối thiểu')]: {
    saving: t('500 nghìn'),
    cd: t('500 nghìn'),
    mf: t('MIN_BALANCE_MF'),
    re: t('20 triệu'),
  },
  [t('Cách thức thanh toán')]: {
    saving: t('Linh hoạt'),
    cd: t('Trả một lần'),
    re: t('Theo tiến độ'),
    mf: t('Trả một lần'),
    reTooltip: t('Thanh toán theo tiến độ của chủ đầu tư dự án, thông thường từ 2-3 năm.'),
  },
  [t('Tất toán lợi nhuận')]: {
    saving: t('Hàng tháng'),
    cd: t('Cuối kỳ'),
    mf: t('Bán ra CCQ'),
    re: t('Bán ra BĐS'),
  },
  [t('Phí quản lý')]: {
    saving: (
      <div>
        <CenterView>{config.saving?.promo_fee ? t('Miễn phí') : t('0.08%/tháng')}</CenterView>
      </div>
    ),
    savingTooltip: t('Phí tính dựa trên giá trị đầu tư.'),
    savingDisableTooltipIcon: true,
    cd: (
      <div>
        <CenterView>{config.cd?.promo_fee ? t('Miễn phí') : t('0.04%/tháng')}</CenterView>
      </div>
    ),
    cdTooltip: t('Phí tính dựa trên giá trị đầu tư.'),
    cdDisableTooltipIcon: true,
    mf: t('Không thu phí'),
    re: t('1,5%/năm'),
    reTooltip: t('Phí tính dựa trên giá trị thanh toán trong năm.'),
  },
})

interface Props {
  reTooltip?: ReactChild
  savingTooltip?: ReactChild
  savingDisableTooltipIcon?: boolean
  cdDisableTooltipIcon?: boolean
  cdTooltip?: ReactChild
  cd: ReactChild
  saving: ReactChild
  re: ReactChild
  bg: ReactChild
  isTitle?: boolean
  tooltip?: ReactChild
}
const RowItem: React.FC<Props> = ({
  saving,
  cd,
  re,
  mf,
  bg = '#fff',
  savingDisableTooltipIcon,
  cdDisableTooltipIcon,
  isTitle,
  reTooltip,
  savingTooltip,
  cdTooltip,
}) => {
  const renderItem = (text: ReactChild, tooltip?: ReactChild, disableTooltipIcon?: boolean) => {
    const content = (
      <Text centered bold lgSize={isTitle ? 22 : 18} size={isTitle ? 18 : 16}>
        {text}
        {tooltip && !disableTooltipIcon ? ' ?⃝' : ''}
      </Text>
    )

    if (tooltip) {
      return (
        <OverlayTrigger placement="top" overlay={(props) => renderTooltip({ ...props, text, tooltip })}>
          {content}
        </OverlayTrigger>
      )
    }

    return content
  }

  return (
    <Box bg={bg} align="center" flex as={Row} height={`${ROW_HEIGTH}px`}>
      <Box height={48} as={Col} xs={3}>
        {renderItem(saving, savingTooltip, savingDisableTooltipIcon)}
      </Box>
      <Box height={48} as={Col} xs={3}>
        {renderItem(cd, cdTooltip, cdDisableTooltipIcon)}
      </Box>
      <Box height={48} as={Col} xs={3}>
        {renderItem(mf)}
      </Box>
      <Box height={48} as={Col} xs={3}>
        {renderItem(re, reTooltip)}
      </Box>
    </Box>
  )
}

const TextWithTooltip = styled(Text)<{ tooltip?: string }>`
  :hover {
    cursor: ${(p) => (p.tooltip ? 'pointer' : '')};
    + div {
      opacity: 1;
    }
  }
`

const renderTooltip = (props: { tooltip: ReactChild; text: ReactChild; [key: string]: any }) => {
  return (
    <Popover
      id={String(props.text)}
      {...props}
      style={{
        ...props.style,
        border: '0',
        boxShadow: '0 2px 20px rgba(0, 46, 137, 0.24)',
      }}
    >
      <Popover.Content>
        <Text size={12} style={{ opacity: 0.6 }}>
          {props.tooltip}
        </Text>
      </Popover.Content>
    </Popover>
  )
}

const ItemMenu: React.FC<{
  tooltip?: string
  text: string
}> = ({ text, tooltip }) => {
  const component = (
    <Box flex align="center" height={`${ROW_HEIGTH}px`}>
      <TextWithTooltip tooltip={tooltip} bold style={{ textTransform: 'uppercase', opacity: 0.6 }}>
        {text}
        {tooltip ? ' ?⃝' : ''}
      </TextWithTooltip>
    </Box>
  )

  if (tooltip) {
    return (
      <OverlayTrigger placement="top" overlay={(props) => renderTooltip({ ...props, text, tooltip })}>
        {component}
      </OverlayTrigger>
    )
  }

  return component
}

const CompareDestkop = observer(() => {
  const [t] = useTranslation()
  const content = getContent(t)
  const menu = useMemo(() => {
    const titles: any = []
    for (let key in content) {
      if (!content[key].isTitle) {
        const tooltip = content[key].tooltip
        titles.push(<ItemMenu key={key} text={key} tooltip={tooltip} />)
      }
    }
    return titles
  }, [content])

  const rowsContent = useMemo(() => {
    const values = Object.values(content)
    return values.map((item, index) => {
      return <RowItem key={String(item.cd)} bg={index % 2 !== 0 ? '#ebf3ff' : '#fff'} isTitle={index === 0} {...item} />
    })
  }, [content])

  return (
    <Content as={Row} flex>
      <Col md={3}>
        <Box style={{ paddingTop: ROW_HEIGTH }}>{menu}</Box>
      </Col>
      <Col md={9} style={{ padding: 0 }}>
        <Box style={{ height: '100%', padding: '0 20px', borderRadius: 24, backgroundColor: '#fff' }}>
          {rowsContent}
        </Box>
      </Col>
    </Content>
  )
})

const MobileRow = styled(Box)`
  height: 60px;
  display: flex;
`

const MobileCol = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  p {
    text-align: center;
    font-weight: bold;
  }
`

const CompareMobile = observer(() => {
  const [t] = useTranslation()
  const content = getContent(t)

  const renderTableContent = ({ sa, cd, re, mf }: { sa?: boolean; cd?: boolean; re?: boolean; mf?: boolean }) => {
    const rows: React.ReactElement[] = []
    for (let key in content) {
      if (!content[key].isTitle) {
        rows.push(
          <Text key={key} bold centered margin="7px 0" style={{ opacity: 0.5, textTransform: 'uppercase' }}>
            {key}
          </Text>
        )
      }
      rows.push(
        <MobileRow key={key + '_content'}>
          {sa && (
            <MobileCol style={{ background: '#edf5fd' }}>
              <Text>{content[key].saving}</Text>
            </MobileCol>
          )}
          {cd && (
            <MobileCol style={{ background: '#e4f1ff' }}>
              <Text>{content[key].cd}</Text>
            </MobileCol>
          )}
          {mf && (
            <MobileCol style={{ background: '#e4f1ff' }}>
              <Text>{content[key].mf}</Text>
            </MobileCol>
          )}
          {re && (
            <MobileCol style={{ background: '#d6eaff' }}>
              <Text>{content[key].re}</Text>
            </MobileCol>
          )}
        </MobileRow>
      )
    }
    return rows
  }

  return (
    <Fragment>
      <Text centered bold size={20}>
        {t('Đầu tư lợi nhuận cố định')}
      </Text>
      <br />
      <Box style={{ width: '100%', borderRadius: 16, overflow: 'hidden', margin: 'auto' }}>
        {renderTableContent({ sa: true, cd: true })}
      </Box>
      <br />
      <Text centered bold size={20}>
        {t('Đầu tư tăng trưởng')}
      </Text>
      <br />
      <Box style={{ width: '100%', borderRadius: 16, overflow: 'hidden', margin: 'auto' }}>
        {renderTableContent({ mf: true, re: true })}
      </Box>
    </Fragment>
  )
})

const Compare = () => {
  return (
    <MyContainer>
      <Text xsStyle={{ marginBottom: 30 }} centered xlSize={56} lgSize={48} size={24} mdSize={36}>
        <Trans i18nKey="So sánh các sản phẩm đầu tư"></Trans>
      </Text>
      <Responsive lg={<CompareDestkop />} xs={<CompareMobile />} />
    </MyContainer>
  )
}

export default Compare

const DiscountBadge = styled(Text)`
  margin-left: 5px;
  bottom: 25px;
  background-color: rgb(234, 32, 103);
  padding: 3px 4px;
  font-size: 10px;
  border-radius: 20px;
  display: inline-block;
  color: white;
  text-transform: uppercase;
  font-weight: bold;
`

const CenterView = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
